import React, { useState, useLayoutEffect, Fragment, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import * as Yup from 'yup';

import UnsavedHeader from './UnsavedHeader';
import CommonBlock from './CommonBlock';
import ShopBlock from './ShopBlock';
import CheckoutBlock from './CheckoutBlock';
import DeliveryBlock from './DeliveryBlock';
import PaymentsBlock from './PaymentsBlock';
import CountragentsBlock from './CountragentsBlock';
import PromocodesBlock from './PromocodesBlock';
import NotificationsBlock from './NotificationsBlock';
import UsersBlock from './UsersBlock';
import FeedsBlock from './FeedsBlock';
import WarehousesBlock from './WarehousesBlock';
import AdditionalAttachmentsBlock from './AdditionalAttachmentsBlock';
import MarginSalesBlock from './MarginSalesBlock';
import EshopsBlock from './EshopsBlock';

import PageTitle from '../common/PageTitle';
import PageMenu from '../common/PageMenu';

import { useFormik } from 'formik';
import {
  fetchUpdateDomainAliasSettings,
  fetchUpdateCheckoutSettings,
  fetchUpdateDeliveryInfoListSettings,
  fetchGetPaymentMethods,
  fetchGetCheckoutSettings,
  fetchUpdatePaymentMethodSettings,
  fetchUpdateAcquaringSettings,
  fetchDeletePaymentMethodSettings,
  fetchPostDeliveryRegionsSettings,
} from '../../actions/settings';
import { fetchGetDeliveryInfo } from '../../actions/orders';
import { toast } from 'react-toastify';
import {
  DELIVERY_TYPE_TRANSLATE,
  PAYMENT_PROVIDER_TYPE,
  PAYMENTS_PAYMENT_METHOD,
} from '../../config';
import { parseError, scrollToBlock } from '../../helpers';
import { fetchGetAccountInfo, fetchPostAccountInfo } from '../../actions/auth';
import FranchiseBlock from './FranchiseBlock';
import { useAutoTranslation } from '../../helpers/useAutoTranslation';

const ContentWrapper = styled('div')`
  position: relative;
  padding-left: 0;
  padding-right: 0;
  padding-top: 19px;
  > div,
  > form > div {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
    padding-bottom: 10px;
  }
  .block-subtitle {
    color: #98a6ad;
    font-size: 11px;
    text-transform: uppercase;
    font-weight: 700;
  }
  .block-anchor {
    position: absolute;
    top: -90px;
  }
  .sub-input {
    font-size: 12px;
    color: #98a6ad;
  }
`;

const Wrapper = styled(`div`)`
  margin-bottom: 300px;
  @media (max-width: 767px) {
    margin: 0 -22px;

    .menu {
      display: none;
    }

    .settings {
      max-width: 100%;
    }

    .page-title-box {
      padding: 0 24px;
    }

    .card-box {
      width: 100%;
      border-radius: unset;
      box-shadow: none;
    }

    .col-6 {
      flex: 100%;
      max-width: 100%;
    }

    .mail-sending {
    }

    .btn.btn-danger {
      margin-top: 15px;
    }

    .feeds-wrapper {
      padding-top: 0 !important;
    }

    .promocodes-row {
      padding-bottom: 16px;
    }

    .row.justify-content-center {
      padding: 0 24px 24px;
    }
  }
`;

const menuData = [
  { title: 'Общие', id: 'common', component: CommonBlock },
  // { title: 'Маржинальность продаж', id: 'margin', component: MarginSalesBlock },
  // { title: 'Магазин', id: 'shop', component: ShopBlock },
  { title: 'Оформление заказа', id: 'checkout', component: CheckoutBlock },
  { title: 'Источники заказов', id: 'eshops', component: EshopsBlock },
  { title: 'Дополнительные вложения', id: 'attachments', component: AdditionalAttachmentsBlock },
  { title: 'Доставка', id: 'delivery', component: DeliveryBlock },
  { title: 'Оплата', id: 'payments', component: PaymentsBlock },
  // { title: 'Склады', id: 'warehouses', component: WarehousesBlock },
  // { title: 'Провайдеры услуг', id: 'franchise', component: FranchiseBlock },
  { title: 'Контрагенты', id: 'countragents', component: CountragentsBlock },
  { title: 'Промокоды', id: 'promocodes', component: PromocodesBlock },
  { title: 'Оповещения', id: 'notifications', component: NotificationsBlock },
  { title: 'Пользователи', id: 'users', component: UsersBlock },
  // { title: 'Фиды для Яндекс.Маркета', id: 'feeds', component: FeedsBlock },
];

const validationSchema = Yup.object().shape({
  domain: Yup.string().matches(/^[^.]+$/, {
    message:
      "Недопустимый символ '.' в имени поддомена. Для разделения слов рекомендуем использовать символы - '_' и '-'",
    excludeEmptyString: true,
  }),
});

const SettingsPage = (props) => {
  const { setCustomHeader } = props;

  const { t } = useAutoTranslation();

  const [currentBlock, setCurrentBlock] = useState({
    title: t('Общие'),
    id: 'common',
    component: CommonBlock,
  });
  const [menuOffset, setMenuOffset] = useState(null);

  const [domainUnsaved, setDomainUnsaved] = useState(false);
  const [checkoutUnsaved, setCheckoutUnsaved] = useState(false);
  const [deliveryInfoUnsaved, setDeliveryInfoUnsaved] = useState(false);
  const [paymentsUnsaved, setPaymentsUnsaved] = useState(false);
  const [acquaringUnsaved, setAcquaringUnsaved] = useState(false);
  const [marginSalesUnsaved, setMarginSalesUnsaved] = useState(false);
  const [ediAddressUnsaved, setEdiAddressUnsaved] = useState(false);

  const [loading, setLoading] = useState(false);

  const [paymentMethods, setPaymentMethods] = useState([]);
  const [changedDeliveries, setChangedDeliveries] = useState([]);
  const dispatch = useDispatch();
  const accountSettings = useSelector((state) => state.auth.account);
  const { checkoutSettings, organizationSettings, paymentSettings } = useSelector(
    (state) => state.settings,
  );

  const marginParams = useSelector((state) => state.auth.account.marginParams);
  const deliveryInfo = useSelector((state) => state.orders.deliveryInfo);
  const isOfertaAccepted = useSelector((state) => state.auth.account.ofertaAccepted);

  const currentDomain = accountSettings.domains.find(
    (d) => !d.auto_generated && !d.external_domain,
  );
  const externalDomain = accountSettings.domains.find(
    (d) => !d.auto_generated && d.external_domain,
  );

  useLayoutEffect(() => {
    const listener = () => {
      if (window.scrollY > 55) {
        const currentOffset = window.scrollY - 55;
        if (currentOffset !== menuOffset) {
          setMenuOffset(currentOffset);
        }
      } else {
        setMenuOffset(null);
      }

      for (let item in menuData) {
        const element = document.getElementById(`settingsBlock${menuData[item].id}`)?.parentElement;
        if (element && element.offsetTop >= window.scrollY - element.offsetHeight) {
          setCurrentBlock(menuData[item]);
          break;
        }
      }
    };
    window.addEventListener('scroll', listener);
    return () => {
      window.removeEventListener('scroll', listener);
    };
  });

  const handleMenuSelect = (block) => {
    scrollToBlock(`settingsBlock${block.id}`);
  };

  const menuStyle = {
    transition: 'margin 150ms',
  };
  if (menuOffset !== null) {
    menuStyle.marginTop = menuOffset + 'px';
  }

  const sortedDeliveryInfo = deliveryInfo
    ? deliveryInfo
        .map((delivery) => {
          return {
            ...delivery,
            visibility: delivery.active
              ? delivery.publicAvailable
                ? 'PUBLIC'
                : 'MANAGERS_ONLY'
              : 'DISABLED',
          };
        })
        .sort((a, b) => {
          if (DELIVERY_TYPE_TRANSLATE[a.type] === DELIVERY_TYPE_TRANSLATE[b.type]) {
            return a.courierTitle > b.courierTitle ? 1 : a.courierTitle < b.courierTitle ? -1 : 0;
          } else {
            return DELIVERY_TYPE_TRANSLATE[a.type] > DELIVERY_TYPE_TRANSLATE[b.type] ? 1 : -1;
          }
        })
    : null;

  const loadPaymentMethods = () => {
    dispatch(fetchGetPaymentMethods()).then((paymentMethods) =>
      setPaymentMethods(paymentMethods || []),
    );
  };

  const formik = useFormik({
    initialValues: {
      //Магазин
      shopName: accountSettings.shopName,
      domain: currentDomain ? currentDomain.domain : '',
      external_domain: externalDomain ? externalDomain.domain : '',

      //Оформление заказа
      uiRequiredPhone: checkoutSettings.uiRequiredFields.indexOf('PHONE') >= 0, //uiRequiredFields
      uiRequiredEmail: checkoutSettings.uiRequiredFields.indexOf('EMAIL') >= 0,
      defaultCity: checkoutSettings.defaultCity,
      freeDeliveryAmount: checkoutSettings.freeDeliveryAmount,
      freeDeliveryRegion: checkoutSettings.freeDeliveryRegion,
      onlySufficientOffers: checkoutSettings.onlySufficientOffers,
      importOrderConfirmed: checkoutSettings.importOrderType === 'APPROVED', //importOrderType "APPROVED" | "NEED_CONFIRM"
      disableCodSumInTariff: checkoutSettings.disableCodSumInTariff,
      disableInsuranceSumInTariff: checkoutSettings.disableInsuranceSumInTariff,
      policyCheckEnabled: checkoutSettings.policyCheckEnabled,
      showConsentToNewsletter: checkoutSettings.showConsentToNewsletter,
      reserveAfterPayment: checkoutSettings.reserveAfterPayment,
      personalInfoPolicyLink: checkoutSettings.personalInfoPolicyLink,
      confidentialPolicyLink: checkoutSettings.confidentialPolicyLink,
      successRedirectUrl: checkoutSettings.successRedirectUrl || '',
      unlockOrderDuringOnlinePayment: checkoutSettings.unlockOrderDuringOnlinePayment || false,
      paymentWithoutCommission: checkoutSettings.paymentWithoutCommission,
      showDeliveryOptions: checkoutSettings.showDeliveryOptions,
      disablePRRInTariff: checkoutSettings.disablePRRInTariff,
      defaultReserveOrders: checkoutSettings.defaultReserveOrders,
      ediAddress: accountSettings.ediAddress,

      //Доставка
      deliveryPremiumAmount: checkoutSettings.deliveryPremiumAmount,
      deliveryPremiumFixAmount: checkoutSettings.deliveryPremiumFixAmount,
      insurancePercent: checkoutSettings.insurancePercent,
      deliveryInfo: sortedDeliveryInfo,
      deliveryRegionSettings: checkoutSettings.deliveryRegionSettings?.commonSettings,

      //Оплата
      payments: [],
      paymentProvider: checkoutSettings.paymentProvider || 'PAYU',
      merchantId: paymentSettings.merchantId,
      accountSecret: paymentSettings.accountSecret,
      accountSecret2: paymentSettings.accountSecret2, //Только для Робокассы
      ipnUrl: paymentSettings.ipnUrl,
      additionalIpnFailUrl: paymentSettings.additionalIpnFailUrl,
      additionalIpnSuccessUrl: paymentSettings.additionalIpnSuccessUrl,

      //Маржинальность продаж
      fulfillmentCost: marginParams.fulfillmentCost,
      marketplaceCosts: marginParams.marketplaceCosts,
      otherCost: marginParams.otherCost,
    },
    validationSchema,

    onSubmit: async (values) => {
      setLoading(true);

      const refreshActions = [];

      if (domainUnsaved) {
        await dispatch(
          fetchUpdateDomainAliasSettings({
            shopName: values.shopName,
            domain: values.domain.toLowerCase(),
            external_domain: values.external_domain.toLowerCase() || null,
          }),
        );
        setDomainUnsaved(false);
      }

      if (paymentsUnsaved) {
        await Promise.all(
          values.payments.map((payment) =>
            dispatch(fetchUpdatePaymentMethodSettings(payment.paymentMethod)),
          ),
        );
        refreshActions.push(loadPaymentMethods);
        setPaymentsUnsaved(false);
      }

      if (checkoutUnsaved || paymentsUnsaved || acquaringUnsaved) {
        const uiRequiredFields = [];
        if (values.uiRequiredPhone) {
          uiRequiredFields.push('PHONE');
        }
        if (values.uiRequiredEmail) {
          uiRequiredFields.push('EMAIL');
        }

        const paymentMethods = values.payments
          .map((payment) => payment.checkoutPayment)
          //Добавляем только те объекты платежей, у кторых checkoutPayment не пустой
          .filter((payment) => payment);

        await dispatch(
          fetchUpdateCheckoutSettings({
            uiRequiredFields,
            defaultCity: values.defaultCity,
            freeDeliveryAmount: values.freeDeliveryAmount,
            freeDeliveryRegion: values.freeDeliveryRegion,
            onlySufficientOffers: values.onlySufficientOffers,
            importOrderType: values.importOrderConfirmed ? 'APPROVED' : 'NEED_CONFIRM',
            disableCodSumInTariff: values.disableCodSumInTariff,
            disableInsuranceSumInTariff: values.disableInsuranceSumInTariff,
            policyCheckEnabled: values.policyCheckEnabled,
            showConsentToNewsletter: values.showConsentToNewsletter,
            reserveAfterPayment: values.reserveAfterPayment,
            showDeliveryOptions: values.showDeliveryOptions,
            disablePRRInTariff: values.disablePRRInTariff,
            defaultReserveOrders: values.defaultReserveOrders,
            personalInfoPolicyLink: values.personalInfoPolicyLink,
            confidentialPolicyLink: values.confidentialPolicyLink,
            deliveryPremiumAmount: values.deliveryPremiumAmount,
            deliveryPremiumFixAmount: values.deliveryPremiumFixAmount,
            insurancePercent: values.insurancePercent,
            paymentProvider: values.paymentProvider,
            paymentWithoutCommission: values.paymentWithoutCommission,
            paymentMethods,
            successRedirectUrl: values.successRedirectUrl,
            unlockOrderDuringOnlinePayment: values.unlockOrderDuringOnlinePayment,
          }),
        );

        const tmpDeliveryRegionSettings =
          values.deliveryRegionSettings?.map((setting) => {
            switch (setting.enabledTmp) {
              case 2:
                setting.enabled = true;
                setting.tariffEnabled = false;
                break;
              case 1:
                setting.enabled = true;
                setting.tariffEnabled = true;
                break;
              case 0:
                setting.enabled = false;
                setting.tariffEnabled = true;
                break;
              default:
                break;
            }
            delete setting.enabledTmp;

            if (setting.country !== `RU`) setting.region = `ALL_REGIONS`;

            return setting;
          }) ?? [];

        if (tmpDeliveryRegionSettings.length) {
          await dispatch(
            fetchPostDeliveryRegionsSettings({
              commonSettings: tmpDeliveryRegionSettings,
            }),
          );
        }

        //Важно чтобы после acquaringUnsaved обновлялся checkout
        refreshActions.push(() => dispatch(fetchGetCheckoutSettings()));
        setCheckoutUnsaved(false);
        setAcquaringUnsaved(false);
      }

      if (deliveryInfoUnsaved) {
        await Promise.all(
          values.deliveryInfo
            .filter((item) => changedDeliveries.includes(item.code))
            .map((delivery) => {
              return dispatch(
                fetchUpdateDeliveryInfoListSettings({
                  code: delivery.code,
                  title: delivery.title,
                  active: delivery.visibility !== 'DISABLED',
                  publicAvailable: delivery.visibility === 'PUBLIC',
                }),
              );
            }),
        );
        refreshActions.push(() => dispatch(fetchGetDeliveryInfo()));
        setDeliveryInfoUnsaved(false);
      }

      if (acquaringUnsaved) {
        const { apiPaymentProvider } = PAYMENT_PROVIDER_TYPE.find(
          (o) => o.value === values.paymentProvider,
        );
        await dispatch(
          fetchUpdateAcquaringSettings(
            {
              merchantId: values.merchantId,
              accountSecret: values.accountSecret,
              ...(values.paymentProvider === 'ROBOKASSA'
                ? { accountSecret2: values.accountSecret2 }
                : {}),
            },
            apiPaymentProvider,
          ),
        );

        setAcquaringUnsaved(false);
      }

      if (marginSalesUnsaved) {
        const body = {
          marginParams: {
            fulfillmentCost: null,
            otherCost: null,
            marketplaceCosts: [],
          },
        };

        if (values.fulfillmentCost) body.marginParams.fulfillmentCost = values.fulfillmentCost;
        if (values.otherCost) body.marginParams.otherCost = values.otherCost;
        if (values.marketplaceCosts) body.marginParams.marketplaceCosts = values.marketplaceCosts;

        await dispatch(fetchPostAccountInfo(body));

        await dispatch(fetchGetAccountInfo());
        setMarginSalesUnsaved(false);
      }

      if (ediAddressUnsaved) {
        const body = {
          ediAddress: values.ediAddress,
        };

        await dispatch(fetchPostAccountInfo(body));
        setEdiAddressUnsaved(false);
      }

      await Promise.all(refreshActions.map((action) => action()));

      toast.info(t('Настройки успешно сохранены'));
      setLoading(false);
    },
  });
  const { values, errors, handleSubmit, setFieldValue, isValid } = formik;

  useEffect(() => {
    setCustomHeader(
      domainUnsaved ||
        checkoutUnsaved ||
        deliveryInfoUnsaved ||
        paymentsUnsaved ||
        acquaringUnsaved ||
        marginSalesUnsaved ||
        ediAddressUnsaved ? (
        <UnsavedHeader handleSubmit={handleSubmit} disabled={!isValid} loading={loading} />
      ) : null,
    );
  }, [
    domainUnsaved,
    checkoutUnsaved,
    deliveryInfoUnsaved,
    paymentsUnsaved,
    acquaringUnsaved,
    marginSalesUnsaved,
    loading,
    isValid,
    ediAddressUnsaved,
  ]);

  useEffect(() => {
    loadPaymentMethods();
  }, []);

  useEffect(() => {
    const findPaymentTypeIndex = (payment) =>
      PAYMENTS_PAYMENT_METHOD.findIndex(
        (elem) => elem.value === payment.paymentMethod.paymentMethod,
      );

    const payments = paymentMethods
      .map((paymentMethod) => {
        const checkoutPayment = checkoutSettings.paymentMethods
          ? checkoutSettings.paymentMethods.find((payment) => payment.code === paymentMethod.code)
          : null;

        return {
          paymentMethod,
          checkoutPayment,
        };
      })

      //Сортировка по индексу типа оплаты в константном массиве типов. Там эти типы расположены именно в том порядке, как требутеся сортировать способы оплаты
      .sort((a, b) => findPaymentTypeIndex(a) - findPaymentTypeIndex(b));

    setFieldValue('payments', payments, false);
    setFieldValue('ipnUrl', checkoutSettings.ipnUrl, false);
    setFieldValue('additionalIpnFailUrl', checkoutSettings.additionalIpnFailUrl, false);
    setFieldValue('additionalIpnSuccessUrl', checkoutSettings.additionalIpnSuccessUrl, false);
  }, [paymentMethods, checkoutSettings]);

  const handleChangeInput = (id, value) => {
    setFieldValue(id, value);

    if (['shopName', 'domain', 'external_domain'].indexOf(id) >= 0) {
      setDomainUnsaved(true);
    } else if (
      [
        'uiRequiredPhone',
        'uiRequiredEmail',
        'defaultCity',
        'freeDeliveryAmount',
        'freeDeliveryRegion',
        'onlySufficientOffers',
        'importOrderConfirmed',
        'disableCodSumInTariff',
        'disableInsuranceSumInTariff',
        'policyCheckEnabled',
        'showConsentToNewsletter',
        'reserveAfterPayment',
        'showDeliveryOptions',
        'defaultReserveOrders',
        'disablePRRInTariff',
        'personalInfoPolicyLink',
        'confidentialPolicyLink',
        'deliveryPremiumAmount',
        'deliveryPremiumFixAmount',
        'insurancePercent',
        'successRedirectUrl',
        'unlockOrderDuringOnlinePayment',
        'deliveryRegionSettings',
      ].indexOf(id) >= 0
    ) {
      setCheckoutUnsaved(true);
    } else if (/^deliveryInfo\./.test(id)) {
      setDeliveryInfoUnsaved(true);
    } else if (['fulfillmentCost', 'otherCost', 'marketplaceCosts'].indexOf(id) >= 0) {
      setMarginSalesUnsaved(true);
    } else if (['ediAddress'].indexOf(id) >= 0) {
      setEdiAddressUnsaved(true);
    }
  };

  //Считается, что payment уже склонирован, здесь не учитывается immutable.
  const handleChangePayment = (payment, acquaring, isNew) => {
    if (isNew) {
      setFieldValue('payments', [...values.payments, payment]);
    } else {
      const idx = values.payments.findIndex(
        (elem) => elem.paymentMethod.code === payment.paymentMethod.code,
      );
      if (idx >= 0) {
        setFieldValue(`payments.${idx}`, payment);
      }
    }

    setPaymentsUnsaved(true);

    if (acquaring) {
      setFieldValue('paymentProvider', acquaring.paymentProvider);
      setFieldValue('merchantId', acquaring.merchantId);
      setFieldValue('accountSecret', acquaring.accountSecret);
      setFieldValue('accountSecret2', acquaring.accountSecret2);
      setFieldValue('paymentWithoutCommission', acquaring.paymentWithoutCommission);
      //URL не редактируются а просто обнуляются при смене платежного провайдера. Они генерируются автоматически API после сохранения данных.
      setFieldValue('ipnUrl', acquaring.ipnUrl);
      setFieldValue('additionalIpnFailUrl', acquaring.additionalIpnFailUrl);
      setFieldValue('additionalIpnSuccessUrl', acquaring.additionalIpnSuccessUrl);
      setAcquaringUnsaved(true);
    }
    handleSubmit();
  };

  const handleDeletePayment = async (payment) => {
    await dispatch(fetchDeletePaymentMethodSettings(payment.paymentMethod.code)).catch((err) => {
      toast.error(parseError(err));
      throw err;
    });

    await Promise.all([loadPaymentMethods(), dispatch(fetchGetCheckoutSettings())]);
    toast.info(t('Способ оплаты успешно удален'));
  };

  /*checkoutSettings.commercialAccount = false;*/

  return (
    <Wrapper>
      <PageTitle title={t('Настройки')} />
      <div className="row">
        <div className="col-9 settings">
          <Fragment>
            {menuData
              .filter((item) => (isOfertaAccepted ? item : item.title !== 'Провайдеры услуг'))
              .map((m) => (
                //В компонентах страницы настроек из accountSettings, checkoutSettings, organizationSettings можно использовать только те параметры, которые не могут редактироваться в этой панели. Либо значения которых в панеле должны обновляться только после сохранения данных. Поскольку они обновляются только после сохранения. А изменяемые значения использовать только данные из formik values.
                <ContentWrapper key={m.id} className="card-box">
                  <m.component
                    accountSettings={accountSettings}
                    checkoutSettings={checkoutSettings}
                    organizationSettings={organizationSettings}
                    values={values}
                    errors={errors}
                    handleChangeInput={handleChangeInput}
                    handleSubmit={handleSubmit}
                    handleChangePayment={handleChangePayment}
                    handleDeletePayment={handleDeletePayment}
                    setChangedDeliveries={setChangedDeliveries}
                  />
                  <div className="block-anchor" id={`settingsBlock${m.id}`} />
                </ContentWrapper>
              ))}
          </Fragment>
        </div>
        <div className="col-3 menu" style={menuStyle}>
          <PageMenu
            menuData={
              isOfertaAccepted
                ? menuData
                : menuData.filter((item) => item.title !== 'Провайдеры услуг')
            }
            current={currentBlock}
            onSelect={handleMenuSelect}
          />
        </div>
      </div>
    </Wrapper>
  );
};

export default SettingsPage;
